import { History } from 'history';
import Store from 'repatch';

import State from '../core/State';
import { demoProjectData } from '../data/demo_project/project';
import PageInfo from '../entity/PageInfo';
import { OrganizationDetailFragment, ProjectForAllOfOrganizationFragment } from '../generated/graphql';
import ErrorPresenter from '../service/ErrorPresenter';
import OrganizationWebApi from '../service/persistent/OrganizationWebApi';
import ProjectWebApi from '../service/persistent/ProjectWebApi';
import { setLastAccessedOrganization } from '../thunk/lastAccessedOrganization';
import {
  disposeLatestProjects,
  finishFetchingLatestProjects,
  setHasDraftItems,
  setLatestProjects,
  startFetchingLatestProjects,
} from '../thunk/latestInsights';

class ListInsightCategoryUsecase {
  private readonly store: Store<State>;
  private readonly history: History;
  private readonly organizationWebApi: OrganizationWebApi;
  private readonly projectWebApi: ProjectWebApi;
  private readonly errorPresenter: ErrorPresenter;

  constructor({
    store,
    history,
    organizationWebApi,
    projectWebApi,
    errorPresenter,
  }: {
    store: Store<State>;
    history: History;
    organizationWebApi: OrganizationWebApi;
    projectWebApi: ProjectWebApi;
    errorPresenter: ErrorPresenter;
  }) {
    this.store = store;
    this.history = history;
    this.organizationWebApi = organizationWebApi;
    this.projectWebApi = projectWebApi;
    this.errorPresenter = errorPresenter;
  }

  // WARN: temporary functions during migration
  public handleSetHasDraftItems = (hasDraftItems: Array<string>) => {
    this.store.dispatch(setHasDraftItems(hasDraftItems));
  };

  // WARN: temporary functions during migration
  public handleSetLastAccessOrganization = (organization: OrganizationDetailFragment) => {
    this.store.dispatch(setLastAccessedOrganization(organization));
  };

  // WARN: temporary functions during migration
  public handleSetLatestProjects = (projects: Array<ProjectForAllOfOrganizationFragment>, pageInfo: PageInfo) => {
    this.store.dispatch(setLatestProjects(projects, pageInfo));
  };

  // WARN: temporary functions during migration
  public handleDisposeLatestProject = () => {
    this.store.dispatch(disposeLatestProjects());
  };

  // WARN: temporary functions during migration
  public handleErrorPresenter = (err: any) => {
    this.errorPresenter.showAsFatal(err);
  };

  public prepareProjects = async (organizationId: string) => {
    let response: { projects: Array<ProjectForAllOfOrganizationFragment>; pageInfo: PageInfo };
    let organization;

    this.store.dispatch(startFetchingLatestProjects());

    try {
      response = await this.projectWebApi.getAllOfOrganization(organizationId, 'all', 'updatedAt', undefined, 3);
      organization = await this.organizationWebApi.getById(organizationId);
    } catch (err) {
      this.store.dispatch(finishFetchingLatestProjects());
      this.store.dispatch(disposeLatestProjects());
      this.errorPresenter.showAsFatal(err);
      return;
    }

    const { projects, pageInfo } = response;
    const demoProjects = demoProjectData.map((v) => v.data.project as ProjectForAllOfOrganizationFragment);
    const projectsWithDemo =
      !pageInfo.hasNextPage && organization.businessRegion.key === 'jp'
        ? [...projects, ...demoProjects].slice(0, 3)
        : projects;

    const hasDraftItems =
      projects.filter &&
      projects
        .filter((project) => {
          const localProject = JSON.parse(window.localStorage.getItem(`project_brief_${project.id}`) || '{}');
          return Object.keys(localProject).length;
        })
        .map((project) => project.id);

    this.store.dispatch(setHasDraftItems(hasDraftItems));
    this.store.dispatch(setLastAccessedOrganization(organization));
    this.store.dispatch(finishFetchingLatestProjects());
    this.store.dispatch(setLatestProjects(projectsWithDemo, pageInfo));
  };

  public dispose = () => {
    this.store.dispatch(disposeLatestProjects());
  };

  public goToProjects = (organizationId?: string) => {
    const path = organizationId ? `/projects?org=${organizationId}` : '/projects';
    this.history.push(path);
  };

  public goToProjectTypes = (organizationId?: string) => {
    const path = organizationId ? `/project_types?org=${organizationId}` : '/project_types';
    this.history.push(path);
  };
}

export default ListInsightCategoryUsecase;
